import React, { useState } from 'react';
import * as HeroIcons from '@heroicons/react/24/outline';
import { ColumnDef } from "@tanstack/react-table"
import { Button } from "components/ui/button"
import { DataTableColumnHeader } from "components/ui/data-table-column-header"
import { createStandardHeader } from "components/ui/standard-column-header"
import { titleCase, upperCase } from "lib/utils"
import { MobileDetailsPopover } from 'components/ui/mobile-details-popover';
import { LhcModal } from './LhcModal';
import { CnDocumentModal } from './CnDocumentModal';
import { NotesModal } from './NotesModal';
import { IncidentModal } from "./IncidentModal";
import { toast } from "sonner";
import { getIncidentByCn, INCIDENT_TYPES } from '../api/incident';

const ActionsCell: React.FC<{ shipment: Shipment; onRefresh?: () => void; onRefreshAlerts?: () => void }> = ({ shipment, onRefresh, onRefreshAlerts }) => {
  const [isLhcModalOpen, setIsLhcModalOpen] = useState<boolean>(false);
  const [isIncidentModalOpen, setIsIncidentModalOpen] = useState<boolean>(false);

  const [incidentData, setIncidentData] = useState<any>(null);
  const [incidentMode, setIncidentMode] = useState<"create" | "view">("create");
  const [loadingIncident, setLoadingIncident] = useState(false);

  const handleTrackShipment = () => {
    if (shipment.tracking_available) {
      const search = window.location.search || ''
      window.open(`/shipments/in-transit/${encodeURIComponent(shipment.cn_number)}${search}`, '_blank');
    }
  };

  const handleIncidentClick = async () => {
    try {
      if ((INCIDENT_TYPES as readonly string[]).includes(shipment.edd)) {
        setLoadingIncident(true);

        const response = await getIncidentByCn(shipment.cn_number);
        setIncidentData(response);
        setIncidentMode("view");
      } else {
        setIncidentMode("create");
        setIncidentData(null);
      }
      setIsIncidentModalOpen(true);
    } catch (error) {
      console.error("Error fetching incident:", error);
      toast.error("Failed to fetch incident data");
    } finally {
      setLoadingIncident(false);
    }
  };

  return (
    <>
      <div className="flex items-center space-x-1">
        <Button
          variant="outline"
          size="sm"
          className={`h-7 px-2.5 py-1 text-xs ${shipment.tracking_available
            ? 'bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200 hover:text-blue-700'
            : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            }`}
          onClick={handleTrackShipment}
          disabled={!shipment.tracking_available}
        >
          <HeroIcons.TruckIcon className="h-4 w-4" />
        </Button>

        <MobileDetailsPopover shipment={shipment} />

        <NotesModal cnNumber={shipment.cn_number} />
        <Button
          variant="outline"
          size="sm"
          className={`h-7 px-2.5 py-1 text-xs ${shipment.edd === "on hold"
            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200 hover:text-blue-700'
          }`}
          onClick={handleIncidentClick}
          disabled={loadingIncident || shipment.edd === "on hold"}
        >
          <HeroIcons.ExclamationTriangleIcon className="h-4 w-4" />
        </Button>
         
        <Button 
          variant="outline" 
          size="sm" 
          className="h-7 px-2.5 py-1 text-xs bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200 hover:text-blue-700"
          onClick={() => setIsLhcModalOpen(true)}
        >
          <HeroIcons.CalendarDaysIcon className="h-4 w-4" />
        </Button>
      </div>

      <LhcModal
        isOpen={isLhcModalOpen}
        onClose={() => setIsLhcModalOpen(false)}
        cnNumber={shipment.cn_number}
        onSuccess={onRefresh}
        onRefreshAlerts={onRefreshAlerts}
      />

      <IncidentModal
        isOpen={isIncidentModalOpen}
        onClose={() => setIsIncidentModalOpen(false)}
        cnNumber={shipment.cn_number}
        mode={incidentMode}
        incidentData={incidentData}
        onSuccess={onRefresh}
      />
    </>
  );
};

const CnNumberCell: React.FC<{ cnNumber: string }> = ({ cnNumber }) => {
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);

  return (
    <div className="flex items-center space-x-2 w-[120px]">
      <span className="font-medium text-xs">{cnNumber}</span>
      <HeroIcons.DocumentTextIcon
        className="h-4 w-4 text-gray-500 cursor-pointer hover:text-gray-800"
        onClick={() => setIsDocModalOpen(true)}
      />
      <CnDocumentModal
        isOpen={isDocModalOpen}
        onClose={() => setIsDocModalOpen(false)}
        cnNumber={cnNumber}
      />
    </div>
  );
};

export interface Shipment {
  cn_number: string;
  cn_date: string;
  branch_name: string;
  consignor: string;
  consignee: string;
  origin: string;
  destination: string;
  edd: string;
  status: string;
  tracking_available: boolean;
  remaining_km: number;
  cn_distance_km: number;
  driver_name?: string | null;
  driver_mobile?: string | null;
  vendor_mobile?: string | null;
}

export const createColumns = (onRefresh?: () => void, onRefreshAlerts?: () => void): ColumnDef<Shipment>[] => [
  {
    accessorKey: "cn_number",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CN Number" />
    ),
    cell: ({ row }) => {
      const cnNumber = row.getValue("cn_number") as string;
      return <CnNumberCell cnNumber={cnNumber} />;
    },
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: "cn_date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CN Date" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.getValue("cn_date"));
      const formatted = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      return <div className="w-[100px] text-xs">{formatted}</div>
    },
    enableSorting: true,
  },
  {
    accessorKey: "branch_name",
    header: createStandardHeader("Branch"),
    cell: ({ row }) => {
      const branchName = row.getValue("branch_name") as string
      return (
        <div className="max-w-[120px] truncate text-xs" title={branchName}>
          {titleCase(branchName)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "consignor",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consignor" />
    ),
    cell: ({ row }) => {
      const consignor = row.getValue("consignor") as string
      return (
        <div className="max-w-[150px] truncate text-xs" title={consignor}>
          {upperCase(consignor)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "consignee",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consignee" />
    ),
    cell: ({ row }) => {
      const consignee = row.getValue("consignee") as string
      return (
        <div className="max-w-[150px] truncate text-xs" title={consignee}>
          {upperCase(consignee)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "origin",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Origin" />
    ),
    cell: ({ row }) => {
      const origin = row.getValue("origin") as string
      return (
        <div className="max-w-[120px] truncate text-xs" title={origin}>
          {titleCase(origin)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "destination",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Destination" />
    ),
    cell: ({ row }) => {
      const destination = row.getValue("destination") as string
      return (
        <div className="max-w-[120px] truncate text-xs" title={destination}>
          {titleCase(destination)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "edd",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="EDD" />
    ),
    cell: ({ row }) => {
      const edd = row.getValue("edd") as string;
      const isOnHold = edd === "on hold";
      const isIncident = (INCIDENT_TYPES as readonly string[]).includes(edd);

      if (isOnHold) {
        return (
          <div className="inline-flex items-center rounded-md px-2.5 py-1 text-xs bg-orange-100 text-orange-600 border border-orange-200">
            On Hold
          </div>
        );
      }

      if (isIncident) {
        return (
          <div className="inline-flex items-center rounded-md px-2.5 py-1 text-xs bg-red-100 text-red-600 border border-red-200">
            {edd}
          </div>
        );
      }

      const date = new Date(edd);
      const formatted = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      const status = row.original.status;
      const isDelayed = status === 'delayed'
      return (
        <div className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs ${isDelayed
          ? 'bg-red-100 text-red-600 border border-red-200'
          : 'bg-green-100 text-green-700 border border-green-200'
          }`}>
          {formatted}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "cn_distance_km",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CN Distance" />
    ),
    cell: ({ row }) => {
      const totalDistance = row.getValue("cn_distance_km");
      return (
        <div className="w-[70px] text-xs">
          {totalDistance ? `${totalDistance} km` : "-"}
        </div>
      );
    },
    enableSorting: true,
  },
  {
    accessorKey: "remaining_km",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Remaining" />
    ),
    cell: ({ row }) => {
      return <div className="w-[60px] text-xs">{`${row.getValue("remaining_km") ? row.getValue("remaining_km") + ` km` : `-`}`}</div>
    },
  },
  {
    id: "actions",
    header: createStandardHeader("Actions"),
    cell: ({ row }) => {
      const shipment = row.original;
      return <ActionsCell shipment={shipment} onRefresh={onRefresh} onRefreshAlerts={onRefreshAlerts} />;
    },
    enableSorting: false,
    enableHiding: false,
  },
];
